import styled, { css, type DefaultTheme } from 'styled-components';
import { Actions, DataTable, SmallSelect, Tag } from '../kfo/ui';
import type { Tone } from './model';

// Стили экрана «ППР по РД»: общие плашки/таблицы/диалоги — из ../kfo/ui,
// здесь только своё (разделы документа, чипы ссылок, ход генерации, ячейки мастера).

const toneColor = (theme: DefaultTheme, tone: Tone): string =>
  tone === 'neutral' ? theme.colors.text.secondary : theme.colors.status[tone];

/** Заметная панель запуска «Новый ППР по РД (ИИ)». */
export const AccentPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: 16px;

  h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(1)};
    font-size: 17px;
  }
  p {
    margin: 0;
    max-width: 760px;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Ряд кнопок документа (выгрузка, аудит, перезапуск) с отступом от вкладок. */
export const Toolbar = styled(Actions)`
  margin: ${({ theme }) => theme.spacing(3)} 0 ${({ theme }) => theme.spacing(4)};
`;

/** Заголовок панели в ряду с кнопками (справочник техники). */
export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  flex: 1;
`;

export const SubTitle = styled.h4`
  margin: ${({ theme }) => theme.spacing(4)} 0 ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
`;

export const Small = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Кнопка-ссылка (раздел в замечании аудитора). */
export const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

// ─────────── Строка черновика в списке ───────────

export const DraftRow = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

export const DraftHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  flex-wrap: wrap;
`;

export const DraftName = styled.span`
  flex: 1;
  min-width: 240px;
`;

export const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  font-size: 13px;
`;

export const ProgressWrap = styled.div`
  width: 220px;
`;

export const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  max-width: 520px;
`;

export const DraftBody = styled.div`
  margin: ${({ theme }) => theme.spacing(3)} 0 ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

// ─────────── Ход генерации ───────────

export const Steps = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)} 22px;
  list-style: none;
  padding: 0;
  margin: 0 0 ${({ theme }) => theme.spacing(3)};

  /* Стрелка между этапами — снаружи «пилюли». Правило на родителе: у этапов с
     разным состоянием разные классы, «& + &» на самом этапе их не связал бы. */
  & > li {
    position: relative;
  }
  & > li + li::before {
    content: '→';
    position: absolute;
    left: -17px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.muted};
    font-weight: 400;
  }
`;

export type StepState = 'done' | 'current' | 'todo' | 'failed';

export const Step = styled.li<{ $state: StepState }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $state }) =>
      $state === 'current'
        ? theme.colors.primary
        : $state === 'done'
          ? theme.colors.status.success
          : $state === 'failed'
            ? theme.colors.status.danger
            : theme.colors.border};
  color: ${({ theme, $state }) =>
    $state === 'current'
      ? '#fff'
      : $state === 'done'
        ? theme.colors.status.success
        : $state === 'failed'
          ? theme.colors.status.danger
          : theme.colors.text.muted};
  background: ${({ theme, $state }) => ($state === 'current' ? theme.colors.primary : 'transparent')};
`;

// ─────────── Разделы документа ───────────

export const SectionBlock = styled.section<{ $level: number; $flash: boolean }>`
  margin: 0 0 ${({ theme }) => theme.spacing(4)} ${({ $level }) => ($level >= 2 ? '28px' : '0')};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme, $flash }) => ($flash ? theme.colors.primary : theme.colors.border)};
  scroll-margin-top: 80px;
  transition: border-color ${({ theme }) => theme.motion.slow}
    ${({ theme }) => theme.motion.easeOut};
  ${({ $flash, theme }) =>
    $flash &&
    css`
      box-shadow: ${theme.shadow.focus};
    `}
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const SectionTitle = styled.strong<{ $level: number }>`
  font-size: ${({ $level }) => ($level >= 2 ? '14px' : '15px')};
  margin-right: auto;
`;

export const Paragraph = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
  line-height: 1.55;
  text-align: justify;
`;

export const Bullets = styled.ul`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  padding-left: 22px;
  font-size: 14px;
  line-height: 1.55;
`;

/** Заглушка «[уточнить: …]» — инженеру видно, что дописать. */
export const Placeholder = styled.mark`
  background: ${({ theme }) => theme.colors.status.warning}29;
  color: inherit;
  border-radius: 3px;
  padding: 0 2px;
`;

export const TableCaption = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing(3)} 0 ${({ theme }) => theme.spacing(1.5)};
`;

/** Таблица раздела: шапка переносится (длинные заголовки граф формы ППР-004). */
export const PprDataTable = styled(DataTable)`
  th {
    white-space: normal;
    vertical-align: bottom;
  }
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: ${({ theme }) => theme.spacing(2)} 0;
`;

const CHIP_TONE: Record<string, Tone> = {
  rd: 'info',
  vol: 'success',
  eq: 'warning',
  calc: 'danger',
  std: 'neutral',
  in: 'neutral',
};

/** Чип ссылки текста на исходные данные; подробности — в подсказке (title). */
export const Chip = styled.span<{ $kind: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 360px;
  font-size: 11px;
  line-height: 1.4;
  padding: 1px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme, $kind }) => toneColor(theme, CHIP_TONE[$kind] ?? 'neutral')}66;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: help;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  b {
    font-weight: 700;
    color: ${({ theme, $kind }) => toneColor(theme, CHIP_TONE[$kind] ?? 'neutral')};
  }
`;

/** Блок «Проверить: …» — предупреждения проверки кода к тексту модели. */
export const WarnBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.status.warning}66;
  background: ${({ theme }) => theme.colors.status.warning}12;
  font-size: 13px;

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }
`;

/** Замечание аудитора: critical — красное, major — оранжевое, minor — серое. */
export const FindingBox = styled.div<{ $tone: Tone }>`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  border-left: 3px solid ${({ theme, $tone }) => toneColor(theme, $tone)};
  background: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.surface : `${toneColor(theme, $tone)}10`};
  font-size: 13px;
  line-height: 1.5;
`;

export const FindingHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: 2px;
`;

export const EditArea = styled.textarea<{ $rows?: number }>`
  width: 100%;
  min-height: ${({ $rows }) => ($rows ?? 10) * 20}px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const InlineBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
`;

// ─────────── Паспорт РД ───────────

export const FactItem = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  line-height: 1.5;

  code {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.text.muted};
    margin-right: 6px;
  }
  details {
    margin-top: 2px;
  }
  summary {
    cursor: pointer;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const Quote = styled.blockquote`
  margin: 4px 0 0;
  padding: 4px 10px;
  border-left: 3px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  white-space: pre-wrap;
`;

// ─────────── Мастер ───────────

export const WizardSteps = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const WizardStep = styled.button<{ $active: boolean; $done: boolean }>`
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.pill};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active, $done }) =>
      $active ? theme.colors.primary : $done ? theme.colors.status.success : theme.colors.border};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ theme, $active, $done }) =>
    $active ? '#fff' : $done ? theme.colors.status.success : theme.colors.text.secondary};

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 ${({ theme }) => theme.spacing(4)};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }

  & > .wide {
    grid-column: 1 / -1;
  }
`;

export const Hint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const WizardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(5)};
  padding-top: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const WideSelect = styled(SmallSelect)`
  max-width: 360px;
`;

/** Вид документа в мастере: полные подписи («ППР ПС — …») не помещаются в 360px. */
export const DocKindSelect = styled(SmallSelect)`
  max-width: 520px;
`;

export const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
  margin: ${({ theme }) => theme.spacing(3)} 0;
`;

export const ErrorList = styled.ul`
  margin: 0;
  padding-left: 18px;
`;

const cellControl = css`
  width: 100%;
  height: 30px;
  padding: 0 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const CellInput = styled.input<{ $invalid?: boolean }>`
  ${cellControl};
  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      border-color: ${theme.colors.status.danger};
    `}
`;

export const CellSelect = styled.select`
  ${cellControl};
  padding: 0 4px;
`;

/** Таблица ввода техники: ширины граф как в «Графике мобилизации техники» ППР-004. */
export const EqTable = styled.table`
  border-collapse: collapse;
  min-width: 1380px;
  width: 100%;
  font-size: 12px;

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 4px 4px;
    text-align: left;
    vertical-align: top;
  }
  th {
    font-weight: 600;
    background: ${({ theme }) => theme.colors.surface};
    vertical-align: bottom;
  }
  th.c-name {
    min-width: 220px;
  }
  th.c-kind {
    min-width: 190px;
  }
  th.c-text {
    min-width: 150px;
  }
  th.c-num {
    min-width: 66px;
  }
  th.c-count {
    min-width: 58px;
  }
  th.c-date {
    width: 140px;
  }
  th.c-method {
    min-width: 150px;
  }
  td.num {
    width: 26px;
    padding-top: 10px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  td.dash {
    color: ${({ theme }) => theme.colors.text.muted};
    padding-top: 10px;
    text-align: center;
  }
  td.actions {
    white-space: nowrap;
  }
  select + input {
    margin-top: 4px;
  }
  tr.sub td {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

export const BelowTable = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

/** Выпадающие подсказки из справочника — поверх диалога, вне прокрутки таблицы. */
export const Suggest = styled.ul.attrs<{ $top: number; $left: number; $width: number }>(
  ({ $top, $left, $width }) => ({ style: { top: $top, left: $left, width: $width } }),
)`
  position: fixed;
  z-index: ${({ theme }) => theme.z.toast};
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.md};
`;

export const SuggestItem = styled.li<{ $active: boolean }>`
  padding: 6px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font-size: 13px;
  line-height: 1.35;
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};

  small {
    display: block;
    font-size: 11px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export const ChartWrap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

// ─────────── Справочные данные ИИ (техника файлом) ───────────

/** Значок «ИИ» у параметра техники; подробности — в подсказке (title) и aria-label. */
export const AiTag = styled(Tag).attrs({ $tone: 'warning' as const })`
  cursor: help;
  vertical-align: middle;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`;

/** «ИИ ×» — снять пометку ИИ со значения, сверенного с паспортом. */
export const AiButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font: inherit;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.02em;
  padding: 0 6px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid currentColor;
  background: transparent;
  color: ${({ theme }) => theme.colors.status.warning};
  white-space: nowrap;
  vertical-align: middle;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.status.warning}1f;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

/** Пояснение к пометкам ИИ в карточке техники и флажок «сверены с паспортом». */
export const AiNote = styled(WarnBox)`
  margin: ${({ theme }) => theme.spacing(1)} 0 ${({ theme }) => theme.spacing(3)};

  label {
    margin: ${({ theme }) => theme.spacing(2)} 0 0;
  }
`;

/** Подпись поля формы со значком («ИИ») в одну строку. */
export const FieldLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

/** Строка пометок под полем ячейки: «ИИ», «из справочника», «из файла: …». */
export const CellMark = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
  margin-top: 3px;
  font-size: 11px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Многострочное поле в ячейке (характеристика техники на проверке). */
export const CellArea = styled.textarea<{ $invalid?: boolean }>`
  ${cellControl};
  display: block;
  height: auto;
  min-height: 46px;
  padding: 5px 6px;
  line-height: 1.4;
  resize: vertical;
  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      border-color: ${theme.colors.status.danger};
    `}
`;

export const ChartTable = styled.table`
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    padding: 3px 6px 3px 0;
    text-align: left;
  }
  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  input {
    width: 96px;
  }
`;
